import { useState, useEffect } from "react";
import { Plus, Trash2 } from "lucide-react";

import {
  Field,
  TextInput,
  TextArea,
  CollapsibleSection,
  CTAGroup,
  ReorderButtons,
  SelectInput,
} from "../../../components/FormField";

import { emptyButton, moveItem } from "./shared";
import { ICON_OPTIONS } from "../../../lib/iconsData";

export function MethodologySections({ setSectionsData, initialData }) {
  /* ---------------- HERO ---------------- */
  const [heroActive, setHeroActive] = useState(true);
  const [heroBadgeText, setHeroBadgeText] = useState("Our Methodology");
  const [heroHeading, setHeroHeading] = useState(
    "How We Deliver — Our Proven Approach to",
  );
  const [heroHeadingEmphasis, setHeroHeadingEmphasis] =
    useState("ERP & CRM Success");
  const [heroDescription, setHeroDescription] = useState("");
  const [heroPrimary, setHeroPrimary] = useState(emptyButton());
  const [heroSecondary, setHeroSecondary] = useState(emptyButton());

  /* ---------------- METHODOLOGY STEPS ---------------- */
  const [methActive, setMethActive] = useState(true);
  const [methBadgeText, setMethBadgeText] = useState("5-Phase Process");
  const [methHeading, setMethHeading] = useState("Our 5-Phase");
  const [methHeadingEmphasis, setMethHeadingEmphasis] = useState(
    "Delivery Methodology",
  );
  const [methDescription, setMethDescription] = useState("");
  const [methSteps, setMethSteps] = useState([
    {
      step: 1,
      title: "",
      timeframe: "",
      icon: "",
      description: "",
      activities: [""],
      outcome: "",
    },
  ]);

  /* ---------------- DIFFERENTIATORS ---------------- */
  const [diffActive, setDiffActive] = useState(true);
  const [diffBadgeText, setDiffBadgeText] = useState("Why ThynkProcess");
  const [diffHeading, setDiffHeading] = useState("Our Approach vs");
  const [diffHeadingEmphasis, setDiffHeadingEmphasis] = useState(
    "The Typical Consultancy",
  );
  const [diffColTypical, setDiffColTypical] = useState(
    "Typical IT Consultancy",
  );
  const [diffColThynk, setDiffColThynk] = useState("ThynkProcess Approach");
  const [diffRows, setDiffRows] = useState([{ typical: "", thynk: "" }]);

  /* ---------------- FINAL CTA ---------------- */
  const [ctaActive, setCtaActive] = useState(true);
  const [ctaHeading, setCtaHeading] = useState("Want to See How We'd");
  const [ctaHeadingEmphasis, setCtaHeadingEmphasis] = useState(
    "Approach Your Project?",
  );
  const [ctaDescription, setCtaDescription] = useState("");
  const [ctaPrimary, setCtaPrimary] = useState(emptyButton());
  const [ctaSecondary, setCtaSecondary] = useState(emptyButton());

  const updateBtn = (setter, key, val) => setter((b) => ({ ...b, [key]: val }));

  /* ---------------- POPULATE FROM API ---------------- */
  useEffect(() => {
    if (!initialData) return;
    const d = initialData;

    if (d.hero) {
      setHeroBadgeText(d.hero.badge_text || "");
      setHeroHeading(d.hero.heading || "");
      setHeroHeadingEmphasis(d.hero.heading_emphasis || "");
      setHeroDescription(d.hero.description || "");
      if (d.hero.primary_cta)
        setHeroPrimary({
          text: d.hero.primary_cta.text || "",
          url: d.hero.primary_cta.link || "",
          newTab: false,
        });
      if (d.hero.secondary_cta)
        setHeroSecondary({
          text: d.hero.secondary_cta.text || "",
          url: d.hero.secondary_cta.link || "",
          newTab: false,
        });
    }

    if (d.methodology_steps) {
      setMethBadgeText(d.methodology_steps.badge_text || "");
      setMethHeading(d.methodology_steps.heading || "");
      setMethHeadingEmphasis(d.methodology_steps.heading_emphasis || "");
      setMethDescription(d.methodology_steps.description || "");
      if (d.methodology_steps.steps?.length)
        setMethSteps(d.methodology_steps.steps);
    }

    if (d.differentiators) {
      setDiffBadgeText(d.differentiators.badge_text || "");
      setDiffHeading(d.differentiators.heading || "");
      setDiffHeadingEmphasis(d.differentiators.heading_emphasis || "");
      setDiffColTypical(d.differentiators.col_typical || "");
      setDiffColThynk(d.differentiators.col_thynk || "");
      if (d.differentiators.rows?.length) setDiffRows(d.differentiators.rows);
    }

    if (d.final_cta) {
      setCtaHeading(d.final_cta.heading || "");
      setCtaHeadingEmphasis(d.final_cta.heading_emphasis || "");
      setCtaDescription(d.final_cta.description || "");
      if (d.final_cta.primary_cta)
        setCtaPrimary({
          text: d.final_cta.primary_cta.text || "",
          url: d.final_cta.primary_cta.link || "",
          newTab: false,
        });
      if (d.final_cta.secondary_cta)
        setCtaSecondary({
          text: d.final_cta.secondary_cta.text || "",
          url: d.final_cta.secondary_cta.link || "",
          newTab: false,
        });
    }
  }, [initialData]);

  const buildPayload = () => ({
    hero: {
      badge_text: heroBadgeText,
      heading: heroHeading,
      heading_emphasis: heroHeadingEmphasis,
      description: heroDescription,
      primary_cta: { text: heroPrimary.text, link: heroPrimary.url },
      secondary_cta: { text: heroSecondary.text, link: heroSecondary.url },
    },
    methodology_steps: {
      badge_text: methBadgeText,
      heading: methHeading,
      heading_emphasis: methHeadingEmphasis,
      description: methDescription,
      steps: methSteps,
    },
    differentiators: {
      badge_text: diffBadgeText,
      heading: diffHeading,
      heading_emphasis: diffHeadingEmphasis,
      col_typical: diffColTypical,
      col_thynk: diffColThynk,
      rows: diffRows,
    },
    final_cta: {
      heading: ctaHeading,
      heading_emphasis: ctaHeadingEmphasis,
      description: ctaDescription,
      primary_cta: { text: ctaPrimary.text, link: ctaPrimary.url },
      secondary_cta: { text: ctaSecondary.text, link: ctaSecondary.url },
    },
  });

  useEffect(() => {
    setSectionsData?.(buildPayload());
  }, [
    heroBadgeText,
    heroHeading,
    heroHeadingEmphasis,
    heroDescription,
    heroPrimary,
    heroSecondary,
    methBadgeText,
    methHeading,
    methHeadingEmphasis,
    methDescription,
    methSteps,
    diffBadgeText,
    diffHeading,
    diffHeadingEmphasis,
    diffColTypical,
    diffColThynk,
    diffRows,
    ctaHeading,
    ctaHeadingEmphasis,
    ctaDescription,
    ctaPrimary,
    ctaSecondary,
  ]);

  /* ---------------- STEP HELPERS ---------------- */
  const updateStep = (i, key, val) =>
    setMethSteps((s) =>
      s.map((x, idx) => (idx === i ? { ...x, [key]: val } : x)),
    );

  const addActivity = (i) =>
    setMethSteps((s) =>
      s.map((x, idx) =>
        idx === i ? { ...x, activities: [...(x.activities || []), ""] } : x,
      ),
    );

  const updateActivity = (stepIdx, actIdx, val) =>
    setMethSteps((s) =>
      s.map((x, idx) =>
        idx === stepIdx
          ? {
              ...x,
              activities: x.activities.map((a, ai) =>
                ai === actIdx ? val : a,
              ),
            }
          : x,
      ),
    );

  const removeActivity = (stepIdx, actIdx) =>
    setMethSteps((s) =>
      s.map((x, idx) =>
        idx === stepIdx
          ? { ...x, activities: x.activities.filter((_, ai) => ai !== actIdx) }
          : x,
      ),
    );

  return (
    <>
      {/* HERO */}
      <CollapsibleSection
        title="Hero Section"
        active={heroActive}
        onToggleActive={setHeroActive}
      >
        <Field label="Badge Text">
          <TextInput
            value={heroBadgeText}
            onChange={(e) => setHeroBadgeText(e.target.value)}
          />
        </Field>
        <Field label="Heading">
          <TextInput
            value={heroHeading}
            onChange={(e) => setHeroHeading(e.target.value)}
          />
        </Field>
        <Field label="Heading Emphasis">
          <TextInput
            value={heroHeadingEmphasis}
            onChange={(e) => setHeroHeadingEmphasis(e.target.value)}
          />
        </Field>
        <Field label="Description">
          <TextArea
            value={heroDescription}
            onChange={(e) => setHeroDescription(e.target.value)}
          />
        </Field>
        <CTAGroup
          label="Primary CTA"
          text={heroPrimary.text}
          url={heroPrimary.url}
          newTab={heroPrimary.newTab}
          onTextChange={(v) => updateBtn(setHeroPrimary, "text", v)}
          onUrlChange={(v) => updateBtn(setHeroPrimary, "url", v)}
          onNewTabChange={(v) => updateBtn(setHeroPrimary, "newTab", v)}
        />
        <CTAGroup
          label="Secondary CTA"
          text={heroSecondary.text}
          url={heroSecondary.url}
          newTab={heroSecondary.newTab}
          onTextChange={(v) => updateBtn(setHeroSecondary, "text", v)}
          onUrlChange={(v) => updateBtn(setHeroSecondary, "url", v)}
          onNewTabChange={(v) => updateBtn(setHeroSecondary, "newTab", v)}
        />
      </CollapsibleSection>

      {/* METHODOLOGY STEPS */}
      <CollapsibleSection
        title="Methodology Steps"
        active={methActive}
        onToggleActive={setMethActive}
      >
        <Field label="Badge Text">
          <TextInput
            value={methBadgeText}
            onChange={(e) => setMethBadgeText(e.target.value)}
          />
        </Field>
        <Field label="Heading">
          <TextInput
            value={methHeading}
            onChange={(e) => setMethHeading(e.target.value)}
          />
        </Field>
        <Field label="Heading Emphasis">
          <TextInput
            value={methHeadingEmphasis}
            onChange={(e) => setMethHeadingEmphasis(e.target.value)}
          />
        </Field>
        <Field label="Description">
          <TextArea
            value={methDescription}
            onChange={(e) => setMethDescription(e.target.value)}
          />
        </Field>

        <div className="flex justify-between mt-6 mb-2">
          <p className="text-xs font-semibold">Phase Steps</p>
          <button
            onClick={() =>
              setMethSteps((s) => [
                ...s,
                {
                  step: s.length + 1,
                  title: "",
                  timeframe: "",
                  icon: "",
                  description: "",
                  activities: [""],
                  outcome: "",
                },
              ])
            }
            className="flex items-center gap-1 text-xs"
          >
            <Plus size={14} /> Add Step
          </button>
        </div>

        {methSteps.map((s, i) => (
          <div key={i} className="border p-3 rounded-lg mb-4 space-y-3">
            {/* Step header */}
            <div className="flex justify-between">
              <p className="text-xs font-semibold">Phase {s.step}</p>
              <div className="flex gap-2">
                <ReorderButtons
                  index={i}
                  total={methSteps.length}
                  onMove={(f, t) =>
                    setMethSteps((st) =>
                      moveItem(st, f, t).map((x, idx) => ({
                        ...x,
                        step: idx + 1,
                      })),
                    )
                  }
                />
                {methSteps.length > 1 && (
                  <button
                    onClick={() =>
                      setMethSteps((st) =>
                        st
                          .filter((_, idx) => idx !== i)
                          .map((x, idx) => ({ ...x, step: idx + 1 })),
                      )
                    }
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <Field label="Title">
                <TextInput
                  value={s.title}
                  onChange={(e) => updateStep(i, "title", e.target.value)}
                />
              </Field>
              <Field label="Timeframe">
                <TextInput
                  value={s.timeframe}
                  placeholder="e.g. Weeks 1–2"
                  onChange={(e) => updateStep(i, "timeframe", e.target.value)}
                />
              </Field>
            </div>

            <Field label="Icon">
              <SelectInput
                value={s.icon}
                options={ICON_OPTIONS}
                onChange={(e) => updateStep(i, "icon", e.target.value)}
              />
            </Field>

            <Field label="Description">
              <TextArea
                value={s.description}
                onChange={(e) => updateStep(i, "description", e.target.value)}
              />
            </Field>

            {/* Activities nested array */}
            <div className="flex justify-between mb-1">
              <p className="text-xs font-semibold">Key Activities</p>
              <button
                onClick={() => addActivity(i)}
                className="flex items-center gap-1 text-xs"
              >
                <Plus size={12} /> Add Activity
              </button>
            </div>
            {s.activities?.map((act, ai) => (
              <div key={ai} className="flex gap-2 items-center">
                <div className="flex-1">
                  <TextInput
                    value={act}
                    placeholder={`Activity ${ai + 1}`}
                    onChange={(e) => updateActivity(i, ai, e.target.value)}
                  />
                </div>
                {s.activities.length > 1 && (
                  <button onClick={() => removeActivity(i, ai)}>
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            ))}

            <Field label="Deliverable / Outcome">
              <TextArea
                value={s.outcome}
                onChange={(e) => updateStep(i, "outcome", e.target.value)}
              />
            </Field>
          </div>
        ))}
      </CollapsibleSection>

      {/* DIFFERENTIATORS */}
      <CollapsibleSection
        title="Differentiators Table"
        active={diffActive}
        onToggleActive={setDiffActive}
      >
        <Field label="Badge Text">
          <TextInput
            value={diffBadgeText}
            onChange={(e) => setDiffBadgeText(e.target.value)}
          />
        </Field>
        <Field label="Heading">
          <TextInput
            value={diffHeading}
            onChange={(e) => setDiffHeading(e.target.value)}
          />
        </Field>
        <Field label="Heading Emphasis">
          <TextInput
            value={diffHeadingEmphasis}
            onChange={(e) => setDiffHeadingEmphasis(e.target.value)}
          />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Left Column Header">
            <TextInput
              value={diffColTypical}
              onChange={(e) => setDiffColTypical(e.target.value)}
            />
          </Field>
          <Field label="Right Column Header">
            <TextInput
              value={diffColThynk}
              onChange={(e) => setDiffColThynk(e.target.value)}
            />
          </Field>
        </div>

        <div className="flex justify-between mt-6 mb-2">
          <p className="text-xs font-semibold">Comparison Rows</p>
          <button
            onClick={() =>
              setDiffRows((s) => [...s, { typical: "", thynk: "" }])
            }
            className="flex items-center gap-1 text-xs"
          >
            <Plus size={14} /> Add Row
          </button>
        </div>
        {diffRows.map((row, i) => (
          <div key={i} className="border p-3 rounded-lg mb-3 space-y-3">
            <div className="flex justify-between">
              <p className="text-xs font-semibold">Row {i + 1}</p>
              <div className="flex gap-2">
                <ReorderButtons
                  index={i}
                  total={diffRows.length}
                  onMove={(f, t) => setDiffRows(moveItem(diffRows, f, t))}
                />
                {diffRows.length > 1 && (
                  <button
                    onClick={() =>
                      setDiffRows((s) => s.filter((_, idx) => idx !== i))
                    }
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            </div>
            <Field label="Typical Consultancy (Left ✗)">
              <TextArea
                value={row.typical}
                onChange={(e) =>
                  setDiffRows((s) =>
                    s.map((x, idx) =>
                      idx === i ? { ...x, typical: e.target.value } : x,
                    ),
                  )
                }
              />
            </Field>
            <Field label="ThynkProcess (Right ✓)">
              <TextArea
                value={row.thynk}
                onChange={(e) =>
                  setDiffRows((s) =>
                    s.map((x, idx) =>
                      idx === i ? { ...x, thynk: e.target.value } : x,
                    ),
                  )
                }
              />
            </Field>
          </div>
        ))}
      </CollapsibleSection>

      {/* FINAL CTA */}
      <CollapsibleSection
        title="Final CTA"
        active={ctaActive}
        onToggleActive={setCtaActive}
      >
        <Field label="Heading">
          <TextInput
            value={ctaHeading}
            onChange={(e) => setCtaHeading(e.target.value)}
          />
        </Field>
        <Field label="Heading Emphasis">
          <TextInput
            value={ctaHeadingEmphasis}
            onChange={(e) => setCtaHeadingEmphasis(e.target.value)}
          />
        </Field>
        <Field label="Description">
          <TextArea
            value={ctaDescription}
            onChange={(e) => setCtaDescription(e.target.value)}
          />
        </Field>
        <CTAGroup
          label="Primary CTA"
          text={ctaPrimary.text}
          url={ctaPrimary.url}
          newTab={ctaPrimary.newTab}
          onTextChange={(v) => updateBtn(setCtaPrimary, "text", v)}
          onUrlChange={(v) => updateBtn(setCtaPrimary, "url", v)}
          onNewTabChange={(v) => updateBtn(setCtaPrimary, "newTab", v)}
        />
        <CTAGroup
          label="Secondary CTA"
          text={ctaSecondary.text}
          url={ctaSecondary.url}
          newTab={ctaSecondary.newTab}
          onTextChange={(v) => updateBtn(setCtaSecondary, "text", v)}
          onUrlChange={(v) => updateBtn(setCtaSecondary, "url", v)}
          onNewTabChange={(v) => updateBtn(setCtaSecondary, "newTab", v)}
        />
      </CollapsibleSection>
    </>
  );
}
